import { store } from '@/store/store'
import { originalApi } from '@/store/api/originalApi'
import { paymentApi } from '@/store/api/paymentApi'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import {
	applyBlackjackRoundState,
	clearBlackjackTable,
	selectBlackjackGame,
	setBlackjackClearing,
	setBlackjackSubmitting,
	showBlackjackResult,
} from '@/store/slices/blackjackSlice'
import { showApiErrorToast } from '@/lib/api-toast'
import { extractApiErrorMessage } from '@/lib/api-message'
import { createIdempotencyKey } from '@/lib/idempotency-key'
import type { BlackjackRoundState } from '@/types/blackjack'
import { SoundManager } from './SoundManager'

const CLEAR_STAGGER_MS = 200
const CLEAR_DURATION_MS = 480
const DEAL_STAGGER_MS = 140
const DEAL_DURATION_MS = 480
const RESULT_REVEAL_PAUSE_MS = 200

function wait(ms: number) {
	return new Promise<void>((resolve) => {
		window.setTimeout(resolve, ms)
	})
}

function clearAnimationMs(cardCount: number) {
	if (cardCount <= 0) {
		return 0
	}

	return (cardCount - 1) * CLEAR_STAGGER_MS + CLEAR_DURATION_MS + 80
}

function dealAnimationMs(hands: number[]) {
	const longest = Math.max(0, ...hands)
	if (longest <= 0) {
		return 0
	}

	return (longest - 1) * DEAL_STAGGER_MS + DEAL_DURATION_MS + 80
}

export class GameController {
	private disposed = false

	constructor() {
		void this.loadActiveRound()
	}

	dispose() {
		this.disposed = true
	}

	private get blackjackState() {
		return selectBlackjackGame(store.getState())
	}

	private get authenticated() {
		return selectIsAuthenticated(store.getState())
	}

	private requireAuth(message: string) {
		if (this.authenticated) {
			return true
		}

		showApiErrorToast(message)
		return false
	}

	private applyRoundState(state: BlackjackRoundState) {
		store.dispatch(applyBlackjackRoundState(state))
		if (state.status === 'win') SoundManager.instance.play('deal')
		return
	}

	private async finishRoundAnimations(state: BlackjackRoundState, animationMs: number) {
		await wait(animationMs)

		if (state.status === 'win' || state.profit > 0) {
			await wait(RESULT_REVEAL_PAUSE_MS)
			if (!this.disposed) {
				store.dispatch(showBlackjackResult())
			}
		}
	}

	private async clearTableIfNeeded() {
		const { dealerHand, playerHand, playerHandTwo } = this.blackjackState
		const cardCount = Math.max(dealerHand.length, playerHand.length, playerHandTwo.length)
		if (cardCount === 0) {
			return
		}

		store.dispatch(setBlackjackClearing(true))
		SoundManager.instance.play('clear')
		await wait(clearAnimationMs(cardCount))

		if (this.disposed) {
			return
		}

		store.dispatch(clearBlackjackTable())
		store.dispatch(setBlackjackClearing(false))
		await wait(40)
	}

	private async loadActiveRound() {
		if (!this.authenticated || this.disposed) {
			return
		}

		try {
			const response = await store.dispatch(originalApi.endpoints.getBlackjackActiveRound.initiate(undefined, { subscribe: false, forceRefetch: true })).unwrap()
			if (this.disposed) {
				return
			}

			if (!response.data.blackjackGame || response.data.status === 'idle') {
				return
			}

			store.dispatch(applyBlackjackRoundState(response.data))
		} catch {
			// Active round is optional on first load.
		}
	}

	async startGame() {
		if (this.blackjackState.isSubmitting || this.blackjackState.isClearing) {
			return
		}

		if (!this.requireAuth('Sign in to place a bet.')) {
			return
		}

		const amount = Number(this.blackjackState.betAmount)
		if (!Number.isFinite(amount) || amount <= 0) {
			showApiErrorToast('Enter a valid bet amount.')
			return
		}

		store.dispatch(setBlackjackSubmitting(true))

		try {
			await this.clearTableIfNeeded()
			if (this.disposed) {
				return
			}

			const response = await store
				.dispatch(
					originalApi.endpoints.startBlackjackRound.initiate({
						currency_code: this.blackjackState.currencyCode,
						amount,
						idempotency_key: createIdempotencyKey(),
					}),
				)
				.unwrap()

			if (this.disposed) {
				return
			}

			SoundManager.instance.play('deal')
			this.applyRoundState(response.data)
			await this.finishRoundAnimations(response.data, dealAnimationMs([response.data.dealerHand.length, response.data.playerHand.length, response.data.playerHandTwo.length]))

			if (!this.disposed) {
				store.dispatch(setBlackjackSubmitting(false))
			}
		} catch (error) {
			store.dispatch(setBlackjackClearing(false))
			store.dispatch(setBlackjackSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to start the blackjack round.')
		}
	}

	async hit() {
		if (this.blackjackState.isSubmitting || this.blackjackState.isClearing || this.blackjackState.phase === 'ready') {
			return
		}

		store.dispatch(setBlackjackSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.hitBlackjack.initiate()).unwrap()
			SoundManager.instance.play('deal')
			this.applyRoundState(response.data)
			await this.finishRoundAnimations(response.data, dealAnimationMs([1]) + DEAL_DURATION_MS)
			store.dispatch(setBlackjackSubmitting(false))
		} catch (error) {
			store.dispatch(setBlackjackSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to hit.')
		}
	}

	async stand() {
		if (this.blackjackState.isSubmitting || this.blackjackState.isClearing || this.blackjackState.phase === 'ready') {
			return
		}

		store.dispatch(setBlackjackSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.standBlackjack.initiate()).unwrap()
			SoundManager.instance.play('flip')
			this.applyRoundState(response.data)
			await this.finishRoundAnimations(response.data, dealAnimationMs([response.data.dealerHand.length]) + DEAL_DURATION_MS)
			store.dispatch(setBlackjackSubmitting(false))
		} catch (error) {
			store.dispatch(setBlackjackSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to stand.')
		}
	}

	async doubleDown() {
		if (this.blackjackState.isSubmitting || this.blackjackState.isClearing || this.blackjackState.phase === 'ready') {
			return
		}

		store.dispatch(setBlackjackSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.doubleBlackjack.initiate()).unwrap()
			SoundManager.instance.play('deal')
			this.applyRoundState(response.data)
			await this.finishRoundAnimations(response.data, dealAnimationMs([response.data.dealerHand.length, 1]) + DEAL_DURATION_MS)
			store.dispatch(setBlackjackSubmitting(false))
		} catch (error) {
			store.dispatch(setBlackjackSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to double down.')
		}
	}

	async split() {
		if (this.blackjackState.isSubmitting || this.blackjackState.isClearing || this.blackjackState.phase === 'ready') {
			return
		}

		store.dispatch(setBlackjackSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.splitBlackjack.initiate()).unwrap()
			SoundManager.instance.play('deal')
			this.applyRoundState(response.data)
			store.dispatch(setBlackjackSubmitting(false))
		} catch (error) {
			store.dispatch(setBlackjackSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to split.')
		}
	}

	async acceptInsurance() {
		if (this.blackjackState.isSubmitting || this.blackjackState.isClearing || this.blackjackState.phase !== 'insurance') {
			return
		}

		store.dispatch(setBlackjackSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.insuranceBlackjack.initiate({ confirm: true })).unwrap()
			SoundManager.instance.play('flip')
			this.applyRoundState(response.data)
			await this.finishRoundAnimations(response.data, DEAL_DURATION_MS)
			store.dispatch(setBlackjackSubmitting(false))
		} catch (error) {
			store.dispatch(setBlackjackSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to accept insurance.')
		}
	}

	async declineInsurance() {
		if (this.blackjackState.isSubmitting || this.blackjackState.isClearing || this.blackjackState.phase !== 'insurance') {
			return
		}

		store.dispatch(setBlackjackSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.insuranceBlackjack.initiate({ confirm: false })).unwrap()
			this.applyRoundState(response.data)
			await this.finishRoundAnimations(response.data, DEAL_DURATION_MS)
			store.dispatch(setBlackjackSubmitting(false))
		} catch (error) {
			store.dispatch(setBlackjackSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to decline insurance.')
		}
	}
}
